import { AuthenticationState } from '@pruforce/common-adminweb-sdk';
import { map } from 'lodash';
import { FC, PropsWithChildren, createContext, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ModuleHierachy } from './types';
// import { incentiveModuleConfig } from '../../modules/Incentive/module-def';
import { useHistory } from 'react-router-dom';
import { pulseLeadsModuleConfig } from 'src/app/modules/PulseLeads/module-def';
import { recruitModuleConfig } from 'src/app/modules/Recruit/module-def';
import { recruitmentModuleConfig } from 'src/app/modules/Recruitment/module-def';
import { applicationModuleConfig } from '../../modules/Application/module-def';
import { configModuleConfig } from '../../modules/Config/module-def';
import { eventsModuleConfig as eventV1ModuleConfig } from '../../modules/Event/module-def';
import { feedbackModuleConfig } from '../../modules/Feedback/module-def';
import { incentiveModuleConfig } from '../../modules/IncentiveV2/module-def';
import { newsModuleConfig } from '../../modules/News/module-def';
import { notificationModuleConfig } from '../../modules/Notification/module-def';
import { operationModuleConfig } from '../../modules/Operation/module-def';
import { opmAdModuleConfig } from '../../modules/OpmAd/module-def';
import { productionModuleConfig } from '../../modules/Production/module-def';
import { reportModuleConfig } from '../../modules/Report/module-def';
import { roleControlModuleConfig } from '../../modules/RoleControl/module-def';
import { resourceModuleConfig } from '../../modules/sales-kit/module-def';
import { surveyModuleConfig } from '../../modules/Survey/module-def';
import { toDoListModuleConfig } from '../../modules/ToDoList/module-def';
import { FORBIDDEN_ERROR_PAGE_PATH } from '../constants';
// PCAAEB-34637: hide event v2 for release
import { trainingModuleConfig } from '@pruforce/learning-adminweb-sdk';
import { systemAdminModuleConfig } from '@pruforce/scheduler-adminweb-sdk';
import { useSelector } from 'react-redux';
import { activityTrackingModuleConfig } from 'src/app/modules/ActivityTracking/module-def';
import { agencyCampaignModuleConfig } from 'src/app/modules/AgencyCampaign/module-def';
import { auditLogModuleConfig } from 'src/app/modules/audit-log/module-def';
import { candidateModuleConfig } from 'src/app/modules/Candidate/module-def';
import { commissionToolModuleConfig } from 'src/app/modules/CommissionTool/module-def';
import { Configuration } from 'src/app/modules/Config/types/config-type';
import { eventModuleConfig } from 'src/app/modules/event-v2/module-def';
import { leadMicrositeModuleConfig } from 'src/app/modules/lead-microsite/module-def';
import { leadFormModuleConfig } from 'src/app/modules/LeadForm/module-def';
import { livestreamModuleConfig } from 'src/app/modules/Livestream/module-def';
import { MandatoryTrainingModuleConfig } from 'src/app/modules/MandatoryTraining/module-def';
import { mdrtModuleConfig } from 'src/app/modules/Mdrt/module-def';
import { mdrtHubModuleConfig } from 'src/app/modules/MdrtHub/module-def';
import { opmPdModuleConfig } from 'src/app/modules/OpmPd/module-def';
import { profileModuleConfig } from 'src/app/modules/Profile/module-def';
import { insightsModuleConfig } from 'src/app/modules/PruleadInsightsReporting/module-def';
import { publicPortalConsentModuleConfig } from 'src/app/modules/public-portal-consent/module-def';
import { qaIncomeModuleConfig } from 'src/app/modules/QAIncome/module-def';
import { secretaryModuleConfig } from 'src/app/modules/Secretary/module-def';
import { simulationToolModuleConfig } from 'src/app/modules/SimulationTool/module-def';
import { trainingConfigPresets } from 'src/app/modules/training/config';
import { RootState } from 'src/redux/store';
import { GlobalHelper } from '../helpers/GlobalHelpers';
import { useJWT } from '../utils';

type ModuleContextInternal = {
  moduleMap: ModuleHierachy[];
};

export const ModuleContext = createContext<ModuleContextInternal>({
  moduleMap: [],
});

const filterModule = (moduleItem: ModuleHierachy) => {
  if (!moduleItem.child?.length) {
    return moduleItem;
  }

  if (
    moduleItem?.enableModule !== false &&
    (moduleItem.enableRead || moduleItem.enableCreate || moduleItem.enableUpdate)
  ) {
    moduleItem.child =
      (moduleItem.child || [])
        .map((item) => filterModule(item))
        .filter((item: any) => item?.enableModule !== false && (!item?.child || item.child?.length > 0)) || [];

    return moduleItem;
  }
  if (moduleItem.enableModule === false) {
    moduleItem.child = [];
  }
  return moduleItem;
};

const checkModuleRenderForChannel = (channelConfig: any, permissions: any, moduleList: any, intl: any): any[] => {
  const array: any = [];
  moduleList.map((item: any) => {
    const module = filterModule(item.moduleConfig(permissions, intl, item.configPresets));
    return !channelConfig || channelConfig.enableModule[item.moduleName] ? array.push(module) : null;
  });
  return array;
};

const ModuleProvider: FC<PropsWithChildren> = ({ children }) => {
  const intl = useIntl();
  GlobalHelper.setGlobalIntlRef(intl);
  const history = useHistory();

  const { user } = useSelector<RootState, AuthenticationState>((state) => state.auth);
  const config = useSelector<RootState, Configuration | undefined>((state) => state.config.data);
  const jwt = useJWT();

  const moduleMap: ModuleHierachy[] = useMemo(() => {
    if (!jwt) {
      return [];
    }

    const permissions = map(jwt.permissions, (_, key) => key);
    const region = jwt.region;
    const channel = jwt.channel;

    let channelConfig;
    try {
      channelConfig = require(`../../../channelConfig/${region.toLowerCase()}_${channel.toLowerCase()}.json`);
    } catch (e) {
      channelConfig = require(`../../../channelConfig/default.json`);
    }

    localStorage.setItem('channelConfig', JSON.stringify(channelConfig));
    const moduleList = [
      { moduleConfig: incentiveModuleConfig, moduleName: 'incentive' },
      { moduleConfig: simulationToolModuleConfig, moduleName: 'simulationTool' },
      { moduleConfig: commissionToolModuleConfig, moduleName: 'commissionTool' },
      // { moduleConfig: incentiveModuleConfigV2, moduleName: 'incentive' },
      { moduleConfig: productionModuleConfig, moduleName: 'production' },
      { moduleConfig: resourceModuleConfig, moduleName: 'resource' },
      { moduleConfig: operationModuleConfig, moduleName: 'operation' },
      { moduleConfig: notificationModuleConfig, moduleName: 'notification' },
      { moduleConfig: eventV1ModuleConfig, moduleName: 'events' },
      // PCAAEB-34637: hide event v2 for release
      { moduleConfig: eventModuleConfig, moduleName: 'events' },
      { moduleConfig: surveyModuleConfig, moduleName: 'survey' },
      { moduleConfig: feedbackModuleConfig, moduleName: 'feedback' },
      { moduleConfig: reportModuleConfig, moduleName: 'report' },
      { moduleConfig: newsModuleConfig, moduleName: 'news' },
      { moduleConfig: toDoListModuleConfig, moduleName: 'todolist' },
      { moduleConfig: trainingModuleConfig, moduleName: 'training', configPresets: trainingConfigPresets },
      { moduleConfig: MandatoryTrainingModuleConfig, moduleName: 'mandatory_training' },
      { moduleConfig: configModuleConfig, moduleName: 'config' },
      { moduleConfig: pulseLeadsModuleConfig, moduleName: 'pulseLeads' },
      { moduleConfig: agencyCampaignModuleConfig, moduleName: 'agencyCampaign' },
      { moduleConfig: recruitModuleConfig, moduleName: 'recruit' },
      { moduleConfig: recruitmentModuleConfig, moduleName: 'recruitment' },
      { moduleConfig: insightsModuleConfig, moduleName: 'insights' },
      { moduleConfig: leadMicrositeModuleConfig, moduleName: 'lead_microsite' },
      { moduleConfig: profileModuleConfig, moduleName: 'profile' },
      { moduleConfig: secretaryModuleConfig, moduleName: 'secretary' },
      { moduleConfig: publicPortalConsentModuleConfig, moduleName: 'public_portal_consent' },
      { moduleConfig: applicationModuleConfig, moduleName: 'application' },
      { moduleConfig: mdrtModuleConfig, moduleName: 'mdrt' },
      { moduleConfig: activityTrackingModuleConfig, moduleName: 'activityTracking' },
      // { moduleConfig: roleControlModuleConfig, moduleName: 'roleControl' },
      { moduleConfig: candidateModuleConfig, moduleName: 'candidate' },
      { moduleConfig: livestreamModuleConfig, moduleName: 'livestream' },
      { moduleConfig: qaIncomeModuleConfig, moduleName: 'qaIncome' },
      { moduleConfig: mdrtHubModuleConfig, moduleName: 'mdrtHub' },
      // { moduleConfig: newHireKitModuleConfig, moduleName: 'newHireKit' },
      // { moduleConfig: customerCampaignModuleConfig, moduleName: 'customerCampaign' },
      { moduleConfig: leadFormModuleConfig, moduleName: 'leadForm' },
      { moduleConfig: opmAdModuleConfig, moduleName: 'opmAd' },
      { moduleConfig: opmPdModuleConfig, moduleName: 'opmPd' },
      { moduleConfig: roleControlModuleConfig, moduleName: 'roleControl' },
      { moduleConfig: systemAdminModuleConfig, moduleName: 'systemAdmin' },
      { moduleConfig: auditLogModuleConfig, moduleName: 'auditLog' },
    ];
    const array = checkModuleRenderForChannel(channelConfig, permissions, moduleList, intl);
    return array;
    // eslint-disable-next-line
  }, [config]);

  useEffect(() => {
    let noPermissionFlag = true;
    moduleMap.forEach((moduleItem) => {
      if (moduleItem.enableRead || moduleItem.enableCreate || moduleItem.enableUpdate) {
        noPermissionFlag = false;
      }
    });
    if (noPermissionFlag) {
      history.push(FORBIDDEN_ERROR_PAGE_PATH);
    }
    // eslint-disable-next-line
  }, [user]);

  return <ModuleContext.Provider value={{ moduleMap }}>{children}</ModuleContext.Provider>;
};

export default ModuleProvider;
